/**
 * THE LOOKUP CACHE ADAPTER — `useQuery` where React Query already lives (#2217).
 *
 * Root's ruling (66585): keep `Lookup` presentational and provider-agnostic;
 * put the orchestration in `packages/experience` when reusable and in the host
 * adapter when host-specific; **retain a compatibility wrapper so existing UI
 * consumers do not fork behaviour**.
 *
 * This is the host half. The policy — what a query is called, when two questions
 * are the same, what a write invalidates, how long an answer is fresh — lives in
 * `@solum/experience/lookupQuery` and is identical in both hosts. Only the
 * `useQuery` call is here, because this is where React Query is.
 *
 * ── THE COMPATIBILITY WRAPPER IS THE POINT ──────────────────────────────────
 * `Lookup` takes a `search(q, facet, type, filters)` prop and calls it. It does
 * not know what a cache is and must not learn: four console surfaces already
 * pass a plain function, and a second code path for "cached lookups" is exactly
 * the fork the ruling forbids.
 *
 * So `cachedSearch` returns something with the SAME SHAPE as the function those
 * four already pass. A caller adopts caching by wrapping its existing search;
 * `Lookup` is unchanged, its own race guard still applies, and a host that never
 * wraps behaves exactly as before.
 *
 * ── WHAT THIS FIXES THAT THE HAND-ROLLED EFFECT CANNOT ──────────────────────
 * `Lookup`'s effect re-runs when the QUESTION changes. It has no way to know a
 * record was created somewhere else, so a freshly created row does not appear in
 * the next lookup until the text changes — the person deletes a character and
 * retypes it to make their own new record show up. `invalidateLookups` is that
 * fix, and it is the half no amount of hardening inside `Lookup` could reach.
 */
import type { QueryClient } from '@tanstack/react-query'
import {
  LOOKUP_GC_MS, LOOKUP_STALE_MS, lookupInvalidations, lookupQueryKey, shouldAsk,
  type LookupQuestion,
} from '@solum/experience'

/** The shape `Lookup` already calls. Unchanged, deliberately. */
export type LookupSearch<TSection, TFilters = unknown> = (
  query: string,
  facet?: string | null,
  type?: string | null,
  filters?: TFilters,
) => TSection[] | Promise<TSection[]>

export interface CachedSearchOptions {
  /** The registry or relation being searched — the stable half of the key. */
  source: string
  /** Below this, nothing is asked. Mirrors `Lookup`'s own `minChars`. */
  minChars?: number
}

/**
 * Wrap a search so identical questions share one in-flight request and one
 * cached answer.
 *
 * `fetchQuery` rather than `useQuery`: the caller is a plain function called
 * from inside `Lookup`'s effect, not a component. React Query still owns
 * dedupe, freshness and eviction — a second call with the same key while the
 * first is in flight returns that same promise rather than issuing a second
 * request, which is the deduplication the ruling asks for.
 */
export function cachedSearch<TSection, TFilters = unknown>(
  client: QueryClient,
  search: LookupSearch<TSection, TFilters>,
  options: CachedSearchOptions,
): LookupSearch<TSection, TFilters> {
  const minChars = options.minChars ?? 0

  return (query, facet, type, filters) => {
    const question: LookupQuestion = {
      source: options.source,
      query,
      facet: facet ?? null,
      type: type ?? null,
      filters: filters ?? null,
    }

    // An unsent request cannot be stale, cannot race and cannot refuse. The
    // rule lives in @solum/experience so both hosts answer it identically.
    if (!shouldAsk(question, minChars)) return []

    return client.fetchQuery({
      queryKey: lookupQueryKey(question) as unknown[],
      queryFn: () => Promise.resolve(search(query, facet, type, filters)),
      staleTime: LOOKUP_STALE_MS,
      gcTime: LOOKUP_GC_MS,
      /* Preserve Lookup's refusal contract. The live host retries ordinary
         reads, but this boundary cannot distinguish a governed 403 after the
         source has deliberately reduced it to a presentation-safe error. A
         retry would send the same refused question twice before showing it. */
      retry: false,
    })
  }
}

/**
 * Make every lookup that could contain `entity` stale.
 *
 * Call it after a write. The prefixes come from `@solum/experience` so a host
 * cannot invent its own invalidation scope and disagree with the other one.
 */
export function invalidateLookups(client: QueryClient, entity: string): Promise<void> {
  return Promise.all(
    lookupInvalidations(entity).map((prefix) =>
      client.invalidateQueries({ queryKey: prefix as unknown[] })),
  ).then(() => undefined)
}
